import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'
import { downloadBlob } from '@sequencehq/utils'
import SpinnerContainer from 'components/Loading/SpinnerContainer'

const DEFERRED_REVENUE_PATH = '/revenue-recognition/deferred-revenue'
const DEFAULT_FILENAME = 'deferred-revenue-schedule.csv'

const DEFERRED_REVENUE_SCHEDULE_FILENAME_PATTERN =
  /^(\d{4}-\d{2}-\d{2}|today)_[\w-]+\.csv$/

export const getFilenameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname
    const filename = pathname.split('/').pop() ?? ''
    const match = filename.match(DEFERRED_REVENUE_SCHEDULE_FILENAME_PATTERN)
    if (!match) {
      Sentry.captureException(
        new Error(
          `Deferred revenue schedule filename did not match expected pattern: ${filename}`
        )
      )
      return DEFAULT_FILENAME
    }
    return `deferred-revenue-schedule_${match[1]}.csv`
  } catch (error) {
    Sentry.captureException(error)
    return DEFAULT_FILENAME
  }
}

export const DeferredRevenueScheduleDownload = () => {
  const [searchParams] = useSearchParams()
  const signature = searchParams.get('signature')
  const navigate = useNavigate()
  const { displayNotification } = useNotifications()

  const { data, isError, isSuccess } = useQuery(
    dashboardv99990101Client.getDeferredRevenueScheduleSignedDownload,
    { signature: signature ?? '' },
    { enabled: !!signature }
  )

  useEffect(() => {
    if (isSuccess && data?.url) {
      ;(async () => {
        try {
          const response = await fetch(data.url)
          if (!response.ok) {
            throw new Error('Download failed')
          }

          const filename = getFilenameFromUrl(data.url)
          const blob = await response.blob()
          downloadBlob(filename)(blob)

          displayNotification('Downloaded successfully', {
            type: 'success'
          })
        } catch {
          displayNotification('Failed to download report', {
            type: 'error'
          })
        } finally {
          navigate(DEFERRED_REVENUE_PATH)
        }
      })()
    } else if (isError || !signature || (isSuccess && !data?.url)) {
      displayNotification('This download link has expired or is invalid', {
        type: 'error'
      })
      navigate(DEFERRED_REVENUE_PATH)
    }
  }, [signature, isSuccess, isError, data, displayNotification, navigate])

  return <SpinnerContainer />
}
